import type { FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useEnrollScaEnrollment,
  useGetServiceProviderPhoenixNoSuspense,
} from '@pflegenavi/service-provider-frontend/api';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { useErrorSnackbar } from '@pflegenavi/web-components';
import { WelcomeSideBar } from '../general/WelcomeSideBar';

/**
 * SCA Enrollment Waiting Screen for service providers
 *
 * Shown when a user has a MangoPay user created (mangopay_user_id exists)
 * but SCA is not yet enrolled (mangopay_sca_enrolled: false).
 *
 * Features:
 * - Polls service provider data every 3 seconds
 * - Shows "Verifying Identity" spinner
 * - "Reopen Verification" button to re-trigger SCA enrollment
 * - Auto-redirects to bank account setup when SCA enrollment completes
 *
 * @example
 * // User is redirected here when mangopay_user_id exists but mangopay_sca_enrolled is false
 * <ScaEnrollmentWaitingScreen />
 */
export const ScaEnrollmentWaitingScreen: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueErrorSnackbar } = useErrorSnackbar();

  const { mutateAsync: enrollSca, isPending: isEnrolling } =
    useEnrollScaEnrollment();

  // Poll service provider data every 3 seconds to detect SCA enrollment completion
  const { data: serviceProvider, refetch } =
    useGetServiceProviderPhoenixNoSuspense({
      refetchInterval: 3000,
      refetchOnWindowFocus: true,
    });

  // Redirect when SCA enrollment is complete
  useEffect(() => {
    if (serviceProvider?.data?.mangopay_sca_enrolled) {
      navigate(spAppMainPages.SETUP_BANK_ACCOUNT, { replace: true });
    }
  }, [serviceProvider?.data?.mangopay_sca_enrolled, navigate]);

  // Refetch on window focus (user returns from MangoPay browser tab)
  useEffect(() => {
    const handleFocus = () => {
      refetch();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refetch]);

  const handleReopenVerification = useCallback(async () => {
    try {
      const returnUrl = `${window.location.origin}${spAppMainPages.SETUP_BANK_ACCOUNT}`;
      const response = await enrollSca(returnUrl);

      if (response.redirect_url) {
        // Add returnUrl query parameter
        const separator = response.redirect_url.includes('?') ? '&' : '?';
        const redirectUrl = `${
          response.redirect_url
        }${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

        // Redirect to MangoPay SCA verification
        window.location.href = redirectUrl;
      } else {
        // No redirect URL means SCA is already enrolled
        navigate(spAppMainPages.SETUP_BANK_ACCOUNT, { replace: true });
      }
    } catch (error) {
      enqueueErrorSnackbar(
        error,
        t('service-provider-app.mangopay.sca-enrollment-error')
      );
    }
  }, [enrollSca, navigate, enqueueErrorSnackbar, t]);

  return (
    <Grid container height="100vh" overflow="hidden">
      <Grid item md={3} height="100vh">
        <WelcomeSideBar
          title={t('service-provider-app.connect-account.hello')}
          subtitle={t(
            'service-provider-app.connect-account.welcome-to-pflegenavi'
          )}
        />
      </Grid>
      <Grid
        item
        md={9}
        height="100vh"
        overflow="auto"
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Stack alignItems="center" gap={4} maxWidth={500} px={4}>
          <CircularProgress size={64} />

          <Typography variant="h5" textAlign="center" color="primary.main">
            {t('service-provider-app.mangopay.web.verifying-identity')}
          </Typography>

          <Typography variant="body1" textAlign="center" color="grey.600">
            {t(
              'service-provider-app.mangopay.web.complete-verification-browser'
            )}
          </Typography>

          <Typography variant="body2" textAlign="center" color="grey.500">
            {t('service-provider-app.mangopay.web.return-when-done')}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleReopenVerification}
            disabled={isEnrolling}
          >
            {isEnrolling ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('service-provider-app.mangopay.web.reopen-verification')
            )}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
};
